"use client";

import { PostalCodeData, getIpData, lookupZipcodeData } from "@/data/zipcode";
import { SentryFeatureTags, logException } from "@punchup/sentry";

import { Database } from "@punchup/data/supabase.types";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import { useQuery } from "@tanstack/react-query";

export function useUserLocation() {
  const queryFn = async (): Promise<PostalCodeData | null> => {
    const supabase = createClientComponentClient<Database>();
    const {
      data: { user },
    } = await supabase.auth.getUser();

    let userZipcode: string | undefined;
    let userCountry: string | undefined;

    // 1. DB Lookup (if logged in)
    if (user) {
      const { data: existingRow, error: locationError } = await supabase
        .from("user_zipcodes")
        .select("zipcode,country_code")
        .eq("user", user.id)
        .eq("from_ip", false)
        .maybeSingle();

      if (locationError) {
        logException(locationError, {
          extra: { userId: user.id },
          tags: { feature: SentryFeatureTags.Feed },
        });
      }

      if (existingRow?.zipcode && existingRow?.country_code) {
        userZipcode = existingRow.zipcode;
        userCountry = existingRow.country_code;
      }
    }

    // 2. IP Lookup (if needed)
    if (!userZipcode || !userCountry) {
      const ipData = await getIpData();
      if (ipData?.postalCode && ipData?.countryCode) {
        userZipcode = ipData.postalCode;
        userCountry = ipData.countryCode;
      }
    }

    // 3. Return null if no location found
    if (!userZipcode || !userCountry) {
      return null;
    }

    // 4. Lookup full postal code data
    const locationData = await lookupZipcodeData(userZipcode, userCountry);
    return locationData;
  };

  const {
    data: location = null,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["user", "location"],
    queryFn,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return { location, isLoading, error };
}
